import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AlertTriangle, Download, ExternalLink } from 'lucide-react'

import { useTenantBranding } from '../../contexts/TenantContext'

/**
 * One document, opened from a link we emailed a client. Public and read-only.
 *
 * SHARED BY THE PROPOSAL AND INVOICE PAGES rather than copied into each. The first version of this
 * pattern — the signed field ticket — was written standalone, and duplicating it twice more would
 * mean three places to get the same three rules right. The differences between the three are the
 * endpoint and the words, so those are props and nothing else is.
 *
 * SHOWS THE DOCUMENT, not a description of it. An earlier cut of the ticket page rendered a summary
 * card with a download button, which is a worse version of the attachment it replaced: the client
 * came to see the document and had to take a second action to see anything. The PDF is the page.
 *
 * WHY A PAGE AND NOT AN ATTACHMENT. Measured against a real Microsoft 365 tenant: the identical
 * message was quarantined with a PDF attached and reached the inbox without one, and a ZIP made no
 * difference. On 2026-08-19 a proposal carrying an attachment and no link at all was scored as
 * high-confidence phishing outright. No bounce is raised for either, so the sender is told
 * "delivered" while the client has nothing.
 *
 * NO LOGIN. The token in the url is the credential, and it is a DIFFERENT column from anything that
 * can sign, approve or pay — so nobody holding this link can do more than read one document.
 */
export default function ClientDocumentPage({
  apiBase,
  path,             // e.g. 'client/proposal' — the segment before /{slug}/{code}
  heading,          // e.g. 'Bid Proposal'
  fallbackName,     // download filename when the server does not supply one
  renderMeta,       // (data) => JSX shown beside the heading
  gone,             // message for an expired or unknown link
}) {
  const { slug, code } = useParams()
  const brand = useTenantBranding()
  const [doc, setDoc] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const base = `${apiBase}/${path}/${encodeURIComponent(slug)}/${encodeURIComponent(code)}`
  // The document endpoint is public and serves `inline`, so it can be framed directly rather than
  // fetched into a blob first — which is what lets the PDF appear without a second click.
  const pdfUrl = `${base}/pdf`

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await fetch(base)
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          // Show the server's words ONLY when they were written for a person. A service-wide 404
          // handler can flatten a route's message into "The endpoint /api/v1/... was not found",
          // and putting that in front of a customer leaks an internal path and explains nothing.
          const detail = typeof data.detail === 'string' ? data.detail : ''
          const looksInternal = detail.includes('/api/') || detail.toLowerCase().includes('endpoint')
          throw new Error(!detail || looksInternal ? gone : detail)
        }
        const data = await res.json()
        if (!cancelled) setDoc(data)
      } catch (e) {
        if (!cancelled) setError(e.message || gone)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [base, gone])

  if (loading) {
    return (
      <Shell brand={brand}>
        <div className="flex h-64 items-center justify-center text-sm text-gray-500">Loading…</div>
      </Shell>
    )
  }

  if (error) {
    return (
      <Shell brand={brand}>
        <div className="mx-auto max-w-md px-4 py-16 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" aria-hidden="true" />
          <p className="mt-3 text-sm text-gray-700">{error}</p>
        </div>
      </Shell>
    )
  }

  const downloadName = doc?.download_name || fallbackName

  return (
    <Shell brand={brand}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <div className="min-w-0">
          <h1 className="truncate text-base font-semibold text-gray-900">{heading}</h1>
          {renderMeta ? <div className="mt-0.5 text-sm text-gray-600">{renderMeta(doc)}</div> : null}
        </div>
        <div className="flex items-center gap-2">
          {/* Plain anchors, not scripted downloads: they work when the framed viewer does not, and
              on a phone they hand the file to whatever the client normally reads PDFs with. */}
          <a
            href={pdfUrl}
            download={downloadName}
            className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Download
          </a>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            Open
          </a>
        </div>
      </div>

      {/* THE DOCUMENT IS THE PAGE. `object` rather than `iframe` because it degrades honestly: a
          browser with no PDF viewer (older mobile Safari, some in-app webviews) renders the children
          below instead of the blank grey rectangle an iframe leaves behind. */}
      <object data={pdfUrl} type="application/pdf" className="h-[calc(100vh-7rem)] w-full bg-gray-100">
        <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
          <p className="text-sm text-gray-700">Your browser can’t display this document here.</p>
          <a
            href={pdfUrl}
            className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Open the PDF
          </a>
        </div>
      </object>
    </Shell>
  )
}

function Shell({ brand, children }) {
  return (
    <div className="min-h-screen bg-gray-50">
      {brand?.company_name && (
        <div className="border-b border-gray-200 bg-white px-4 py-2">
          <p className="text-sm font-medium text-gray-600">{brand.company_name}</p>
        </div>
      )}
      {children}
    </div>
  )
}
